import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_SIZES } from "@shared/ui/Button";
import IconButton from "@shared/ui/IconButton";
import { Textarea } from "@shared/ui/Inputs";
import { PencilSimple } from "phosphor-react";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useIntl } from "react-intl";

import { CUSTOM_FIELD_MAX_TEXT_CONTENT_LENGTH } from "#/src/constants";

const CustomTextField = ({
  field,
  handleTextFieldSave,
  isEditable = true,
  dropdownClassname = "",
}) => {
  const { messages } = useIntl();

  const triggerRef = useRef();
  const menuRef = useRef();
  const cTextField = useRef({});

  const [isOpen, setIsOpen] = useState(false);
  const [charCount, setCharCount] = useState(field?.value?.length);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const toggle = () => {
    setIsOpen((prev) => {
      if (!prev && !cTextField.current[field?._id]?.length) {
        cTextField.current[field?._id] = field?.value;
        setCharCount(field?.value?.length);
      }
      if (!prev && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        // The panel is fixed-positioned with a 360px max-width and opens to the
        // right of `left`. When the trigger sits near the right edge (e.g. the
        // ticket sidebar) aligning to its left pushes the panel off-screen, so
        // clamp `left` to keep the panel's right edge within the viewport.
        const DROPDOWN_WIDTH = 360;
        const MARGIN = 8;
        const maxLeft = window.innerWidth - DROPDOWN_WIDTH - MARGIN;
        const left = Math.max(MARGIN, Math.min(rect.left, maxLeft));
        setPos({ top: rect.bottom + 4, left });
      }

      return !prev;
    });
  };

  const updateTextField = (textFields) => {
    cTextField.current = textFields;
    toggle();
  };

  useEffect(() => {
    if (!isOpen) return;
    const onClick = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    const t = setTimeout(
      () => document.addEventListener("mousedown", onClick),
      0,
    );
    window.addEventListener("scroll", onScroll, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (field) {
      setCharCount(field.value?.length || 0);
    }
  }, [field]);

  const isValidLink = (text) => {
    if (!text) return false;

    // Check for standard HTTP(S) links
    try {
      const url = new URL(text);
      if (url.protocol === "http:" || url.protocol === "https:") return true;
    } catch (error) {
      // not a standard URL
    }

    // Check for custom URI schemes (e.g. ridder://, slack://, zoho://)
    return /^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//.test(text);
  };

  const renderWithLinks = (text) => {
    const isHttp = text.startsWith("http://") || text.startsWith("https://");

    return (
      <a
        href={text}
        target={isHttp ? "_blank" : "_self"}
        rel={isHttp ? "noopener noreferrer" : undefined}
        className="text-brand"
        onClick={(e) => e.stopPropagation()}
        style={{ textDecoration: "underline", textTransform: "lowerCase" }}
      >
        {text}
      </a>
    );
  };

  return (
    <>
      <div className="flex items-center gap-sm py-sm custom-field-row">
        <BodyText
          size={BODY_TEXT_SIZES.X_SMALL}
          color="text-secondary"
          title={field?.label}
          className="w-[140px] flex-shrink-0 overflow-hidden text-ellipsis whitespace-nowrap no-styles"
        >
          {field?.label ?? field?.fieldId?.label}:
        </BodyText>
        <div className="flex-1 min-w-0">
          <div className={`c-dropdown ${isOpen ? "c-dropdown--shown" : ""}`}>
            <button
              ref={triggerRef}
              className={`c-textarea-btn u-width-100 ${
                !isEditable
                  ? `${
                      isValidLink(field?.value) ? "" : "pointer-events-none"
                    } !bg-transparent`
                  : "cursor-pointer"
              }`}
              onClick={() => {
                if (!isEditable) return;
                if (isValidLink(field?.value)) return;
                toggle();
              }}
            >
              <span
                className={field?.value ? "u-text-color-gray-v2-100" : ""}
                style={{ whiteSpace: "pre-line" }}
              >
                {field?.value
                  ? isValidLink(field?.value)
                    ? renderWithLinks(field.value)
                    : field.value
                  : `${messages?.common?.emptyProperty}`}
              </span>
            </button>

            {isValidLink(field?.value) && isEditable && (
              <IconButton
                className="ml-2xs"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isEditable) return;
                  toggle();
                }}
              >
                <PencilSimple size={16} className="text-secondary" />
              </IconButton>
            )}
            {isOpen &&
              createPortal(
                <div
                  ref={(node) => {
                    menuRef.current = node;
                    if (node) {
                      node.style.setProperty("position", "fixed", "important");
                      node.style.setProperty(
                        "left",
                        `${pos.left}px`,
                        "important",
                      );
                      node.style.setProperty("z-index", "9999", "important");
                      node.style.setProperty(
                        "display",
                        "inline-flex",
                        "important",
                      );
                      node.style.setProperty("width", "auto", "important");
                      node.style.setProperty("max-width", "360px", "important");
                      // Clamp vertically so a trigger near the bottom (e.g. the
                      // ticket sidebar) doesn't push the panel past the viewport
                      // bottom; flip above the trigger if there isn't room below.
                      const MARGIN = 8;
                      const height = node.getBoundingClientRect().height;
                      const top =
                        pos.top + height > window.innerHeight - MARGIN
                          ? Math.max(
                              MARGIN,
                              window.innerHeight - height - MARGIN,
                            )
                          : pos.top;
                      node.style.setProperty("top", `${top}px`, "important");
                    }
                  }}
                  className={
                    "c-dropdown-menu customize-field-dropdown property-userDropdownMenu flex-col text-area-field-wrapper " +
                    dropdownClassname
                  }
                >
                  <div>
                    <BodyText
                      size={BODY_TEXT_SIZES.X_SMALL}
                      color="text-secondary"
                      className="mb-sm"
                    >
                      {charCount || 0}/{CUSTOM_FIELD_MAX_TEXT_CONTENT_LENGTH}
                    </BodyText>
                    <div className="w-80">
                      <Textarea
                        maxLength={CUSTOM_FIELD_MAX_TEXT_CONTENT_LENGTH}
                        placeholder={
                          messages?.tickets?.renderSchedule.addTextHere
                        }
                        value={cTextField.current[field?._id] || ""}
                        onChange={(e) => {
                          const { value } = e.target;
                          cTextField.current[field?._id] = value;
                          setCharCount((prev) => value.length);
                        }}
                        className="h-6xl"
                      />
                    </div>
                    <div className="flex justify-end mt-md">
                      <Button
                        size={BUTTON_SIZES.SMALL}
                        text={messages?.common?.save}
                        onClick={(e) => {
                          handleTextFieldSave(
                            e,
                            cTextField.current,
                            field?._id,
                            updateTextField,
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>,
                document.body,
              )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomTextField;
